import React, { useEffect, useMemo, useState } from 'react';
import StockChart from '../../../components/StockChart';
import { Pagination } from '../../../components/common/Pagination';
import { calculateMA } from '../../../shared/utils/calculations';
import { useTradingHistory } from '../hooks/useTradingHistory';
import type { TradingSession } from '../types';
import { TradingSessionList } from './TradingSessionList';

type NoteFormState = {
  entryReason: string;
  riskManagement: string;
  confidence: 1 | 2 | 3 | 4 | 5 | null;
  emotion: 'confident' | 'nervous' | 'excited' | 'fearful' | 'greedy' | null;
  exitReason: string;
  lessonLearned: string;
};

const createInitialNoteForm = (note?: any): NoteFormState => ({
  entryReason: note?.entryReason || '',
  riskManagement: note?.riskManagement || '',
  confidence: note?.confidence ?? null, // null 또는 undefined일 때만 null
  emotion: note?.emotion ?? null, // null 또는 undefined일 때만 null
  exitReason: note?.exitReason || '',
  lessonLearned: note?.lessonLearned || '',
});

interface TradeNoteEditorProps {
  trade: any;
  onSave: (form: NoteFormState) => void;
  onCancel: () => void;
}

const TradeNoteEditor: React.FC<TradeNoteEditorProps> = ({
  trade,
  onSave,
  onCancel,
}) => {
  const [form, setForm] = useState<NoteFormState>(() =>
    createInitialNoteForm(trade.note)
  );

  // trade가 바뀔 때마다 초기값으로 리셋
  useEffect(() => {
    setForm(createInitialNoteForm(trade.note));
  }, [trade.id, trade.note]);

  return (
    <div className="trade-note-edit" onClick={e => e.stopPropagation()}>
      <div className="trade-note-edit-form">
        {/* 진입/청산 이유 */}
        {trade.type === 'buy' ? (
          <div>
            <div className="trade-note-field-label">
              <span>📌 진입 이유</span>
            </div>
            <textarea
              className="trade-note-textarea"
              value={form.entryReason}
              onChange={e =>
                setForm(prev => ({ ...prev, entryReason: e.target.value }))
              }
              rows={3}
              maxLength={500}
            />
          </div>
        ) : (
          <div>
            <div className="trade-note-field-label">
              <span>🚪 청산 이유</span>
            </div>
            <textarea
              className="trade-note-textarea"
              value={form.exitReason}
              onChange={e =>
                setForm(prev => ({ ...prev, exitReason: e.target.value }))
              }
              rows={3}
              maxLength={500}
            />
          </div>
        )}

        {/* 리스크 관리 */}
        <div>
          <div className="trade-note-field-label">
            <span>🛡️ 리스크 관리</span>
          </div>
          <textarea
            className="trade-note-textarea"
            value={form.riskManagement}
            onChange={e =>
              setForm(prev => ({ ...prev, riskManagement: e.target.value }))
            }
            rows={2}
            maxLength={500}
          />
        </div>

        {/* 확신도 & 감정 */}
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 120px' }}>
            <div className="trade-note-field-label">
              <span>⭐ 확신도 (1~5)</span>
            </div>
            <select
              className="trade-note-select"
              value={form.confidence ?? ''}
              onChange={e =>
                setForm(prev => ({
                  ...prev,
                  confidence: e.target.value === '' ? null : (Number(e.target.value) as 1 | 2 | 3 | 4 | 5),
                }))
              }
            >
              <option value="">선택 안 함</option>
              <option value={1}>1 - 매우 낮음</option>
              <option value={2}>2 - 낮음</option>
              <option value={3}>3 - 보통</option>
              <option value={4}>4 - 높음</option>
              <option value={5}>5 - 매우 높음</option>
            </select>
          </div>

          <div style={{ flex: '1 1 140px' }}>
            <div className="trade-note-field-label">
              <span>😊 감정 상태</span>
            </div>
            <select
              className="trade-note-select"
              value={form.emotion ?? ''}
              onChange={e =>
                setForm(prev => ({
                  ...prev,
                  emotion: e.target.value === '' ? null : (e.target.value as NoteFormState['emotion']),
                }))
              }
            >
              <option value="">선택 안 함</option>
              <option value="confident">자신감</option>
              <option value="nervous">긴장</option>
              <option value="excited">흥분</option>
              <option value="fearful">두려움</option>
              <option value="greedy">욕심</option>
            </select>
          </div>
        </div>

        {/* 배운 점 */}
        <div>
          <div className="trade-note-field-label">
            <span>💡 이번 거래에서 배운 점</span>
          </div>
          <textarea
            className="trade-note-textarea"
            value={form.lessonLearned}
            onChange={e =>
              setForm(prev => ({ ...prev, lessonLearned: e.target.value }))
            }
            rows={3}
            maxLength={500}
          />
        </div>

        {/* 버튼 영역 */}
        <div className="trade-note-edit-footer">
          <button
            className="trade-note-save-btn"
            onClick={e => {
              e.stopPropagation();
              onSave(form);
            }}
          >
            저장
          </button>
          <button
            className="trade-note-cancel-btn"
            onClick={e => {
              e.stopPropagation();
              onCancel();
            }}
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
};

interface TradingHistoryPanelProps {
  visible: boolean;
  onClose: () => void;
  user: any;
  apiBaseUrl: string;
  theme?: 'dark' | 'light';
  selectedUserKey?: number;
  selectedNickname?: string; // 선택된 사용자의 닉네임
  onBackToMyPage?: () => void;
  backButtonText?: string; // 돌아가기 버튼 텍스트 커스터마이징
}

export const TradingHistoryPanel: React.FC<TradingHistoryPanelProps> = ({
  visible,
  onClose,
  user,
  apiBaseUrl,
  theme = 'dark',
  selectedUserKey,
  selectedNickname,
  onBackToMyPage,
  backButtonText,
}) => {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [showOHLCInfo, setShowOHLCInfo] = useState(true);
  const [editingTradeId, setEditingTradeId] = useState<string | null>(null);

  const {
    sessions: apiSessions,
    currentSession,
    isLoading,
    error,
    loadSessionDetail,
    saveTradingNote,
    currentPage,
    totalPages,
    changePage,
    isLimited,
    maxAllowed,
  } = useTradingHistory({
    apiBaseUrl,
    viewerUserKey: user?.userKey,
    targetUserKey: selectedUserKey,
  });

  const [localSessions, setLocalSessions] = useState<TradingSession[]>([]);

  useEffect(() => {
    setLocalSessions(apiSessions);
  }, [apiSessions]);

  // 로그아웃 시 상태 초기화
  useEffect(() => {
    if (!user) {
      setSelectedSessionId(null);
      setShowOHLCInfo(true);
      setEditingTradeId(null);
      setLocalSessions([]);
    }
  }, [user]);

  const sessions = localSessions;

  // 다른 사람의 매매내역을 보고 있는지 확인
  const isViewingOtherUser =
    typeof selectedUserKey === 'number' && selectedUserKey !== user?.userKey;

  const handleSessionSelect = async (sessionId: string) => {
    console.log(
      '세션 선택:',
      sessionId,
      '사용자:',
      selectedUserKey ?? user?.userKey
    );
    setSelectedSessionId(sessionId);
    const existingSession = sessions.find(
      (s: TradingSession) => s.id === sessionId
    );
    await loadSessionDetail(sessionId, existingSession);
  };

  const handleBackToList = () => {
    setSelectedSessionId(null);
    setEditingTradeId(null);
  };

  const handleTradeClick = (trade: any) => {
    // 다른 사람의 내역이면 편집 불가
    if (isViewingOtherUser) {
      return;
    }

    if (editingTradeId === trade.id) {
      setEditingTradeId(null);
      return;
    }

    setEditingTradeId(trade.id);
  };

  const handleSaveNote = (tradeId: string, form: NoteFormState) => {
    // 1) 먼저 로컬 상태를 즉시 업데이트하여 저장된 것처럼 보이게 함 (낙관적 업데이트)
    setEditingTradeId(null);

    if (selectedSessionId) {
      const updatedSessions = sessions.map(session => {
        if (session.id === selectedSessionId) {
          const updatedTrades: typeof session.trades = session.trades.map(
            trade => {
              if (trade.id === tradeId) {
                  return {
                    ...trade,
                    note: {
                      id: tradeId,
                      tradeId: tradeId,
                      entryReason: form.entryReason,
                      riskManagement: form.riskManagement,
                      confidence: form.confidence ?? undefined,
                      emotion: form.emotion ?? undefined,
                      exitReason: form.exitReason,
                      lessonLearned: form.lessonLearned,
                      createdAt: new Date(),
                      updatedAt: new Date(),
                    },
                  };
              }
              return trade;
            }
          );

          return {
            ...session,
            trades: updatedTrades,
          };
        }
        return session;
      });

      setLocalSessions(updatedSessions);
    }

    // 2) 실제 API 호출은 비동기로 수행 (UI는 기다리지 않음)
    (async () => {
      try {
        // null을 명시적으로 전달하여 서버에서 null로 저장 (필드 삭제)
        await saveTradingNote(tradeId, {
          ...form,
          confidence: form.confidence === null ? null : form.confidence,
          emotion: form.emotion === null ? null : form.emotion,
        });
      } catch (error) {
        console.error('메모 저장 실패:', error);
        // TODO: 필요하다면 여기서 토스트/알림으로 사용자에게 저장 실패를 알려줄 수 있음
      }
    })();
  };

  const handleCancelNote = () => {
    setEditingTradeId(null);
  };

  const selectedSession = selectedSessionId
    ? (() => {
        const localSession = sessions.find(
          (s: TradingSession) => s.id === selectedSessionId
        );
        if (localSession) {
          if (
            currentSession &&
            currentSession.id === selectedSessionId &&
            currentSession.chartHistory?.length > 0
          ) {
            return {
              ...localSession,
              chartHistory: currentSession.chartHistory,
            };
          }
          return localSession;
        }
        return currentSession && currentSession.id === selectedSessionId
          ? currentSession
          : null;
      })()
    : null;

  const ma5 = useMemo(
    () =>
      selectedSession?.chartHistory
        ? calculateMA(selectedSession.chartHistory, 5)
        : [],
    [selectedSession?.chartHistory]
  );
  const ma10 = useMemo(
    () =>
      selectedSession?.chartHistory
        ? calculateMA(selectedSession.chartHistory, 10)
        : [],
    [selectedSession?.chartHistory]
  );
  const ma20 = useMemo(
    () =>
      selectedSession?.chartHistory
        ? calculateMA(selectedSession.chartHistory, 20)
        : [],
    [selectedSession?.chartHistory]
  );
  const ma60 = useMemo(
    () =>
      selectedSession?.chartHistory
        ? calculateMA(selectedSession.chartHistory, 60)
        : [],
    [selectedSession?.chartHistory]
  );
  const ma120 = useMemo(
    () =>
      selectedSession?.chartHistory
        ? calculateMA(selectedSession.chartHistory, 120)
        : [],
    [selectedSession?.chartHistory]
  );

  if (!visible) {
    console.log('TradingHistoryPanel: visible=false, 렌더링 안 함');
    return null;
  }

  console.log('TradingHistoryPanel 렌더링:', {
    visible,
    selectedUserKey,
    viewerUserKey: user?.userKey,
    isOtherUser: typeof selectedUserKey === 'number',
  });

  return (
    <section className="trading-history-section">
      {selectedSessionId &&
      selectedSession &&
      selectedSession.chartHistory &&
      selectedSession.chartHistory.length > 0 ? (
        <div
          className="trading-history-container"
          style={{
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto',
            padding: '0 clamp(1rem, 4vw, 2rem)',
            boxSizing: 'border-box',
            border: '1px solid var(--border-color)',
            borderRadius: '18px',
            background: 'var(--bg-primary)',
            overflow: 'hidden',
          }}
        >
          <div className="trading-history-header">
            <div
              style={{
                display: 'flex',
                gap: '1.5rem',
                width: '100%',
                padding: '0.6rem clamp(1rem, 4vw, 2rem)',
                boxSizing: 'border-box',
                margin: 0,
              }}
            >
              <div
                className="trading-history-title"
                style={{
                  flex: '1 1 70%',
                  minWidth: 0,
                  padding: '0.6rem 0',
                }}
              >
                <span
                  className="trading-history-icon"
                  style={{ marginLeft: '1.2rem' }}
                >
                  📊
                </span>
                <div style={{ marginLeft: '0.75rem' }}>
                  <h2>
                    {selectedUserKey
                      ? `${selectedNickname || '플레이어'}의 매매내역`
                      : '매매내역 복기'}
                  </h2>
                  <p>
                    {selectedUserKey
                      ? `${
                          selectedNickname || '플레이어'
                        }의 거래 기록을 분석하고 투자 실력을 향상시켜보세요`
                      : '거래 기록을 분석하고 투자 실력을 향상시켜보세요'}
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginLeft: 'auto',
                    marginRight: '1.2rem',
                  }}
                >
                  <button onClick={handleBackToList} className="logout-btn">
                    ← 목록으로
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="trading-history-body"
            style={{
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: '0.5rem',
              paddingBottom: '1.5rem',
              maxWidth: '100%',
              margin: '0 auto',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '1.5rem',
                width: '100%',
                maxWidth: '1600px',
                margin: '0 auto',
                padding: '0 clamp(1rem, 4vw, 2rem)',
                boxSizing: 'border-box',
              }}
            >
              <div
                className="chart-card"
                style={{
                  flex: '1 1 70%',
                  minWidth: 0,
                  height: '650px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div className="chart-header-row">
                  <div className="chart-header-left">
                    <span className="chart-title">
                      {selectedUserKey
                        ? `${selectedNickname || '플레이어'}의 매매내역 차트`
                        : '매매내역 복기 차트'}
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span className="chart-subinfo">
                        종목: {selectedSession.stockName} (
                        {selectedSession.stockCode}) | 거래일:{' '}
                        {selectedSession.startDate} ~ {selectedSession.endDate}{' '}
                        | 거래횟수: {selectedSession.trades?.length || 0}회
                      </span>
                      <div className="chart-controls" style={{ margin: 0 }}>
                        <label className="ohlc-toggle">
                          <input
                            type="checkbox"
                            checked={showOHLCInfo}
                            onChange={e => setShowOHLCInfo(e.target.checked)}
                          />
                          <span>OHLC 정보 표시</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="chart-container"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    height: 'auto',
                  }}
                >
                  <div className="chart-wrapper">
                    <div className="candlestick-wrapper">
                      <StockChart
                        data={selectedSession.chartHistory.map(candle => ({
                          time: candle.timestamp,
                          open: candle.open,
                          high: candle.high,
                          low: candle.low,
                          close: candle.close,
                          volume: candle.volume,
                        }))}
                        volumeData={selectedSession.chartHistory.map(
                          candle => ({
                            time: candle.timestamp,
                            value: candle.volume,
                          })
                        )}
                        ma5={ma5}
                        ma10={ma10}
                        ma20={ma20}
                        ma60={ma60}
                        ma120={ma120}
                        tradingMarkers={(() => {
                          if (
                            !selectedSession.trades ||
                            selectedSession.trades.length === 0
                          ) {
                            return [];
                          }

                          // 거래 timestamp를 "해당 날짜의 캔들 time"으로 정규화 (일봉 기준)
                          // - 마커 time이 캔들 time과 정확히 일치하지 않으면 1봉씩 밀려 보일 수 있음
                          const resolveCandleTime = (tradeTimestamp: number) => {
                            const candle = selectedSession.chartHistory?.find(
                              c => Math.abs(c.timestamp - tradeTimestamp) < 86400000
                            );
                            return candle?.timestamp ?? tradeTimestamp;
                          };

                          const markerMap = new Map<
                            string,
                            {
                              time: number;
                              type: 'buy' | 'sell';
                              price: number;
                              quantity: number;
                              profit?: number;
                              note?: string;
                              tradeCount: number;
                            }
                          >();

                          selectedSession.trades.forEach(trade => {
                            const candleTime = resolveCandleTime(trade.timestamp);
                            const tradeDate = new Date(candleTime);
                            const dayKey = `${tradeDate.getFullYear()}-${tradeDate.getMonth()}-${tradeDate.getDate()}-${
                              trade.type
                            }`;

                            if (markerMap.has(dayKey)) {
                              const existing = markerMap.get(dayKey)!;
                              const totalValue =
                                existing.price * existing.quantity +
                                trade.price * trade.quantity;
                              existing.quantity += trade.quantity;
                              existing.price = totalValue / existing.quantity;
                              existing.tradeCount += 1;
                              if (
                                trade.type === 'sell' &&
                                trade.profit !== undefined
                              ) {
                                existing.profit = trade.profit;
                              }
                              if (!existing.note && trade.note?.entryReason) {
                                existing.note = trade.note.entryReason;
                              }
                            } else {
                              markerMap.set(dayKey, {
                                time: candleTime,
                                type: trade.type,
                                price: trade.price,
                                quantity: trade.quantity,
                                profit: trade.profit,
                                note: trade.note?.entryReason,
                                tradeCount: 1,
                              });
                            }
                          });

                          const markers = Array.from(markerMap.values()).map(
                            marker => ({
                              time: marker.time,
                              type: marker.type,
                              price: marker.price,
                              quantity: marker.quantity,
                              profit: marker.profit,
                              note:
                                marker.tradeCount > 1
                                  ? `${marker.note || ''} (${
                                      marker.tradeCount
                                    }회 거래)`.trim()
                                  : marker.note,
                            })
                          );

                          return markers;
                        })()}
                        theme={theme}
                        showOHLCInfo={showOHLCInfo}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="trading-history-list-panel"
                style={{
                  flex: '0 1 300px',
                  minWidth: '280px',
                  maxWidth: '350px',
                  background: 'var(--bg-card)',
                  borderRadius: '18px',
                  boxShadow: '0 4px 32px var(--shadow)',
                  padding: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '650px',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                }}
              >
                <h3
                  style={{
                    margin: '0 0 1rem 0.5rem',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    color: 'var(--text-primary)',
                    borderBottom: '2px solid var(--border-color)',
                    paddingBottom: '0.5rem',
                    wordBreak: 'keep-all',
                    overflowWrap: 'break-word',
                  }}
                >
                  📋 매매내역 목록
                </h3>
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    paddingTop: '0.25rem',
                    paddingBottom: '0.25rem',
                    paddingLeft: '0.5rem',
                    paddingRight: '0.5rem',
                  }}
                >
                  {selectedSession.trades &&
                  selectedSession.trades.length > 0 ? (
                    selectedSession.trades
                      .sort((a, b) => a.timestamp - b.timestamp)
                      .map((trade, index) => {
                        const tradeDate = new Date(trade.timestamp);
                        const formattedTime = `${
                          tradeDate.getMonth() + 1
                        }/${tradeDate.getDate()} ${tradeDate
                          .getHours()
                          .toString()
                          .padStart(2, '0')}:${tradeDate
                          .getMinutes()
                          .toString()
                          .padStart(2, '0')}`;
                        const totalAmount = trade.price * trade.quantity;

                        return (
                          <div
                            key={trade.id || index}
                            style={{
                              padding: '0.75rem',
                              background:
                                theme === 'dark'
                                  ? 'rgba(255, 255, 255, 0.05)'
                                  : 'rgba(0, 0, 0, 0.03)',
                              borderRadius: '8px',
                              border: `1px solid ${
                                trade.type === 'buy'
                                  ? 'rgba(38, 166, 154, 0.3)'
                                  : 'rgba(239, 83, 80, 0.3)'
                              }`,
                              transition: 'all 0.2s ease',
                              cursor: isViewingOtherUser ? 'default' : 'pointer',
                              width: '100%',
                              boxSizing: 'border-box',
                              overflow: 'visible',
                              minWidth: 0,
                            }}
                            onClick={() => handleTradeClick(trade)}
                            onMouseEnter={e => {
                              // 다른 사람의 내역이면 호버 효과 없음
                              if (!isViewingOtherUser) {
                                e.currentTarget.style.background =
                                  theme === 'dark'
                                    ? 'rgba(255, 255, 255, 0.1)'
                                    : 'rgba(0, 0, 0, 0.05)';
                                e.currentTarget.style.transform =
                                  'translateX(4px)';
                                e.currentTarget.style.cursor = 'pointer';
                              }
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background =
                                theme === 'dark'
                                  ? 'rgba(255, 255, 255, 0.05)'
                                  : 'rgba(0, 0, 0, 0.03)';
                              e.currentTarget.style.transform = 'translateX(0)';
                              e.currentTarget.style.cursor = isViewingOtherUser ? 'default' : 'pointer';
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '0.5rem',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '0.85rem',
                                  fontWeight: 'bold',
                                  color:
                                    trade.type === 'buy'
                                      ? '#26a69a'
                                      : '#ef5350',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: '0 1 auto',
                                  minWidth: 0,
                                }}
                              >
                                {trade.type === 'buy' ? '▲ 매수' : '▼ 매도'}
                              </span>
                              <span
                                style={{
                                  fontSize: '0.75rem',
                                  color: 'var(--text-muted)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: '0 1 auto',
                                  minWidth: 0,
                                  marginLeft: '0.5rem',
                                }}
                              >
                                {formattedTime}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: '0.8rem',
                                color: 'var(--text-secondary)',
                                lineHeight: '1.6',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  marginBottom: '0.25rem',
                                  gap: '0.5rem',
                                }}
                              >
                                <span style={{ flex: '0 0 auto', minWidth: 0 }}>
                                  가격:
                                </span>
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                  }}
                                >
                                  ₩{Math.floor(trade.price).toLocaleString()}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  marginBottom: '0.25rem',
                                  gap: '0.5rem',
                                }}
                              >
                                <span style={{ flex: '0 0 auto', minWidth: 0 }}>
                                  수량:
                                </span>
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                  }}
                                >
                                  {trade.quantity}주
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  marginBottom: '0.25rem',
                                  gap: '0.5rem',
                                }}
                              >
                                <span style={{ flex: '0 0 auto', minWidth: 0 }}>
                                  총액:
                                </span>
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                  }}
                                >
                                  ₩{totalAmount.toLocaleString()}
                                </span>
                              </div>
                              {trade.profit !== undefined && (
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginTop: '0.5rem',
                                    paddingTop: '0.5rem',
                                    borderTop: `1px solid var(--border-color)`,
                                    gap: '0.5rem',
                                  }}
                                >
                                  <span
                                    style={{ flex: '0 0 auto', minWidth: 0 }}
                                  >
                                    수익률:
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 'bold',
                                      color:
                                        trade.profit > 0
                                          ? '#26a69a'
                                          : '#ef5350',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      minWidth: 0,
                                    }}
                                  >
                                    {trade.profit > 0 ? '+' : ''}
                                    {trade.profit.toFixed(2)}%
                                  </span>
                                </div>
                              )}
                              {trade.note && editingTradeId !== trade.id && (
                                <div
                                  style={{
                                    marginTop: '0.75rem',
                                    paddingTop: '0.75rem',
                                    borderTop: `1px solid var(--border-color)`,
                                  }}
                                >
                                  {trade.type === 'buy' &&
                                    trade.note.entryReason && (
                                      <div style={{ marginBottom: '0.75rem' }}>
                                        <div
                                          style={{
                                            fontSize: '0.65rem',
                                            fontWeight: 'bold',
                                            color: 'var(--text-secondary)',
                                            marginBottom: '0.25rem',
                                          }}
                                        >
                                          📌 진입 이유
                                        </div>
                                        <div
                                          style={{
                                            fontSize: '0.75rem',
                                            color: 'var(--text-primary)',
                                            padding: '0.5rem',
                                            background:
                                              theme === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.03)',
                                            borderRadius: '6px',
                                            fontWeight: '500',
                                            lineHeight: '1.5',
                                            whiteSpace: 'pre-wrap',
                                          }}
                                        >
                                          {trade.note.entryReason}
                                        </div>
                                      </div>
                                    )}
                                  {trade.type === 'sell' &&
                                    trade.note.exitReason && (
                                      <div style={{ marginBottom: '0.75rem' }}>
                                        <div
                                          style={{
                                            fontSize: '0.65rem',
                                            fontWeight: 'bold',
                                            color: 'var(--text-secondary)',
                                            marginBottom: '0.25rem',
                                          }}
                                        >
                                          🚪 청산 이유
                                        </div>
                                        <div
                                          style={{
                                            fontSize: '0.75rem',
                                            color: 'var(--text-primary)',
                                            padding: '0.5rem',
                                            background:
                                              theme === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.03)',
                                            borderRadius: '6px',
                                            fontWeight: '500',
                                            lineHeight: '1.5',
                                            whiteSpace: 'pre-wrap',
                                          }}
                                        >
                                          {trade.note.exitReason}
                                        </div>
                                      </div>
                                    )}

                                  {/* 리스크 관리 */}
                                  {trade.note.riskManagement && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                      <div
                                        style={{
                                          fontSize: '0.65rem',
                                          fontWeight: 'bold',
                                          color: 'var(--text-secondary)',
                                          marginBottom: '0.25rem',
                                        }}
                                      >
                                        🛡️ 리스크 관리
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '0.75rem',
                                          color: 'var(--text-primary)',
                                          padding: '0.5rem',
                                          background:
                                            theme === 'dark'
                                              ? 'rgba(255, 255, 255, 0.05)'
                                              : 'rgba(0, 0, 0, 0.03)',
                                          borderRadius: '6px',
                                          fontWeight: '500',
                                          lineHeight: '1.5',
                                          whiteSpace: 'pre-wrap',
                                        }}
                                      >
                                        {trade.note.riskManagement}
                                      </div>
                                    </div>
                                  )}

                                  {/* 메모 내용 추가 표시 */}
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: '1rem',
                                      fontSize: '0.75rem',
                                      color: 'var(--text-secondary)',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {trade.note.confidence && (
                                      <div>
                                        <strong>확신도:</strong>{' '}
                                        {'⭐'.repeat(trade.note.confidence)}
                                      </div>
                                    )}
                                    {trade.note.emotion && (
                                      <div>
                                        <strong>감정:</strong>{' '}
                                        {trade.note.emotion === 'confident'
                                          ? '자신감'
                                          : trade.note.emotion === 'nervous'
                                          ? '긴장'
                                          : trade.note.emotion === 'excited'
                                          ? '흥분'
                                          : trade.note.emotion === 'fearful'
                                          ? '두려움'
                                          : '욕심'}
                                      </div>
                                    )}
                                  </div>

                                  {/* 배운 점 */}
                                  {trade.note.lessonLearned && (
                                    <div style={{ marginTop: '0.5rem' }}>
                                      <div
                                        style={{
                                          fontSize: '0.65rem',
                                          fontWeight: 'bold',
                                          color: 'var(--text-secondary)',
                                          marginBottom: '0.25rem',
                                        }}
                                      >
                                        💡 배운 점
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '0.75rem',
                                          color: 'var(--text-primary)',
                                          padding: '0.4rem',
                                          background:
                                            theme === 'dark'
                                              ? 'rgba(255, 255, 255, 0.03)'
                                              : 'rgba(0, 0, 0, 0.02)',
                                          borderRadius: '4px',
                                          whiteSpace: 'pre-wrap',
                                        }}
                                      >
                                        {trade.note.lessonLearned}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* 편집 폼은 이전에 정의된 것과 동일하게 유지 */}
                              {/* 다른 사람의 내역이 아닐 때만 편집 폼 표시 */}
                              {!isViewingOtherUser && editingTradeId === trade.id && (
                                <TradeNoteEditor
                                  trade={trade}
                                  onSave={form =>
                                    handleSaveNote(trade.id, form)
                                  }
                                  onCancel={handleCancelNote}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <div
                      style={{
                        textAlign: 'center',
                        color: 'var(--text-muted)',
                        padding: '2rem 0',
                      }}
                    >
                      거래 내역이 없습니다
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="trading-history-header">
            <div
              style={{
                display: 'flex',
                gap: '1.5rem',
                width: '100%',
                padding: '0.6rem clamp(1rem, 4vw, 2rem)',
                boxSizing: 'border-box',
                margin: 0,
              }}
            >
              <div
                className="trading-history-title"
                style={{ flex: '1 1 70%', minWidth: 0, padding: '0.6rem 0' }}
              >
                <span
                  className="trading-history-icon"
                  style={{ marginLeft: '1.2rem' }}
                >
                  📊
                </span>
                <div style={{ marginLeft: '0.75rem' }}>
                  <h2>매매내역 복기</h2>
                  <p>
                    {selectedUserKey
                      ? `${
                          selectedNickname || '플레이어'
                        }의 거래 기록을 분석하고 투자 실력을 향상시켜보세요`
                      : '거래 기록을 분석하고 투자 실력을 향상시켜보세요'}
                  </p>
                </div>
                <button
                  onClick={onBackToMyPage || onClose}
                  className="logout-btn"
                  style={{
                    marginLeft: 'auto',
                    marginRight: '1.2rem',
                  }}
                >
                  {backButtonText ||
                    (onBackToMyPage ? '← 마이페이지로' : '← 게임 화면으로')}
                </button>
              </div>
            </div>
          </div>
          <div
            className="trading-history-body"
            style={{
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: '0.5rem',
              paddingBottom: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {selectedSessionId && isLoading ? (
              <div
                className="loading-state"
                style={{
                  margin: '0 auto',
                  width: '100%',
                  maxWidth: '1200px',
                }}
              >
                <div className="loading-spinner">⏳</div>
                <p>차트 데이터를 불러오는 중...</p>
              </div>
            ) : selectedSessionId &&
              !isLoading &&
              selectedSession &&
              (!selectedSession.chartHistory ||
                selectedSession.chartHistory.length === 0) ? (
              <div
                className="error-state"
                style={{
                  margin: '0 auto',
                  width: '100%',
                  maxWidth: '1200px',
                }}
              >
                <p>
                  {selectedUserKey
                    ? '차트 데이터를 불러올 수 없습니다. 해당 사용자의 매매내역이 비공개이거나 데이터가 없을 수 있습니다.'
                    : '차트 데이터를 불러올 수 없습니다.'}
                </p>
                {error && (
                  <p style={{ color: '#ef4444', marginTop: '0.5rem' }}>
                    {error}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                  <TradingSessionList
                    sessions={sessions}
                    isLoading={isLoading}
                    error={error}
                    onSessionSelect={handleSessionSelect}
                    onClose={onBackToMyPage || onClose}
                    isLimited={isLimited}
                    maxAllowed={maxAllowed}
                  />
                </div>
                {!isLoading && !error && sessions.length > 0 && (
                  <div
                    style={{
                      flexShrink: 0,
                      marginTop: 'auto',
                      paddingTop: '1.5rem',
                      width: '100%',
                    }}
                  >
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={changePage}
                      isLimited={isLimited}
                      maxAllowed={maxAllowed}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
};
